import { useEffect, useRef } from "react";
import type { SignInConfig, UsbChoiceOption } from "../scenario/types";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";

/**
 * Episode 4A ("Secure sign-in") — the workstation's lock screen, with the
 * password already sitting there (masked) because it's the exact one from
 * the Episode 3 sticky note. A GENUINE branching decision on what to do
 * ABOUT that reuse, not just whether to sign in. Choices never show
 * VERIFY/RISKY spoilers — the learner decides blind, exactly like UsbDevice.
 */
export function SignIn({
  config,
  onEnded,
}: {
  config: SignInConfig;
  onEnded: (key: string, choiceId: string, eventId?: string) => void;
}) {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    audio.logon();
  }, []);

  const pick = (opt: UsbChoiceOption) => {
    // 2026-07-25 feedback: the same correct/incorrect quiz chime every other
    // decision game uses (see PrivacySort.tsx / UsbDevice.tsx), not a generic
    // buzzer/click.
    if (opt.key === "risky") audio.chimeBad();
    else audio.chimeGood();
    onEnded(opt.key, opt.id, opt.eventId);
  };

  return (
    <div className="signin">
      <div className="signin-panel">
        <div className="signin-lock" aria-hidden>
          {"\u{1F512}"}
        </div>
        <div className="signin-screen">{config.screenTitle ?? "NorthBay Financial \u2014 Sign in"}</div>
        <div className="signin-user">{config.userName ?? "A. Morgan"}</div>
        <div className="signin-pwrow">
          <span className="signin-pwdots">{config.passwordMask ?? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}</span>
          <span className="signin-pwtick" aria-hidden>
            {"\u2713"}
          </span>
        </div>
        {config.noteText && <p className="signin-note">{config.noteText}</p>}
        <ChoiceList prompt="What do you do?">
          {config.options.map((opt) => (
            <ChoiceCard
              key={opt.id}
              category={opt.key === "risky" ? "comply" : "verify"}
              reply={opt.label}
              showRiskLabel={false}
              onClick={() => pick(opt)}
            />
          ))}
        </ChoiceList>
      </div>
    </div>
  );
}
